import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { usePullToRefresh } from '../hooks/usePullToRefresh';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({ onRefresh, children }) => {
  const { isRefreshing, pullDistance, shouldShowRefreshIndicator } = usePullToRefresh(onRefresh);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Pull to refresh indicator */}
      <Box
        sx={{
          position: 'fixed',
          top: shouldShowRefreshIndicator || isRefreshing ? 0 : -80,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          display: shouldShowRefreshIndicator || isRefreshing ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          color: 'primary.main',
          px: 1.5,
          py: 0.5,
          borderRadius: '0 0 12px 12px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
          backdropFilter: 'blur(4px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          minWidth: '80px',
          maxWidth: '100px',
        }}
      >
        {isRefreshing ? (
          <CircularProgress size={16} sx={{ color: 'primary.main' }} />
        ) : (
          <>
            <RefreshIcon 
              sx={{ 
                fontSize: 16, 
                mb: 0.25,
                transform: `rotate(${pullDistance * 2}deg)`,
                transition: 'transform 0.2s ease-out',
                opacity: Math.min(pullDistance / 40, 1),
              }} 
            />
            <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'center', fontSize: '10px' }}>
              {pullDistance > 60 ? 'Release' : 'Pull'}
            </Typography>
          </>
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          transform: `translateY(${Math.min(pullDistance * 0.5, 40)}px)`,
          transition: pullDistance === 0 ? 'transform 0.3s ease-out' : 'none',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default PullToRefresh;